import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'

import Breadcrumbs from '../../../components/breadcrumbs'
import Footer from '../../../components/footer'
import { getCanonicalUrl } from '../../../utils/canonical'
import DarkNav from '../../../components/dark-nav'
import './zapier-feature.css'

const CreateContact = () => {
  const [showScrollTop, setShowScrollTop] = useState(false)

  useEffect(() => {
    if (typeof AOS !== 'undefined') {
      AOS.init({
        duration: 800,
        easing: 'ease-out',
        once: false,
        offset: 100,
        delay: 0,
        mirror: true
      })
    }

    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300)
      if (typeof AOS !== 'undefined') {
        AOS.refresh()
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Integrations' },
    { label: 'Zapier', path: '/integrations/zapier' },
    { label: 'Create Contact' }
  ]

  const useCases = [
    {
      title: "Auto-Add E-commerce Customers to SMS Lists",
      industry: "E-commerce & Retail",
      description: "Automatically add new Australian Shopify/WooCommerce customers to your DataFlows contact groups for future SMS campaigns.",
      trigger: "Shopify - New Customer",
      steps: [
        "Customer completes first purchase on Shopify",
        "Extract: Name, phone number, email, location",
        "Filter: Only Australian phone numbers (starts with +61 or 04)",
        "DataFlows creates contact in 'VIP Customers' group",
        "Send welcome SMS: 'Thanks for shopping with us! Reply STOP to opt out'",
        "Future marketing campaigns can target this group"
      ]
    },
    {
      title: "CRM to SMS Contact Sync",
      industry: "Sales & Marketing",
      description: "Sync new leads from Salesforce, HubSpot, or Pipedrive to DataFlows contact lists automatically.",
      trigger: "Salesforce - New Lead",
      steps: [
        "Sales team adds new lead to Salesforce",
        "Extract: First name, last name, mobile number, lead source",
        "DataFlows creates contact with custom fields: Lead Source, Date Added",
        "Add to 'Warm Leads' contact group",
        "Trigger automated SMS nurture sequence",
        "Sales team can send SMS directly from DataFlows dashboard"
      ]
    },
    {
      title: "Event Registration to SMS List Builder",
      industry: "Events & Conferences",
      description: "Build SMS contact lists from event registrations for sending updates, reminders, and post-event follow-ups.",
      trigger: "Eventbrite - New Order",
      steps: [
        "Attendee registers for 'Sydney Marketing Summit 2025'",
        "Extract: Name, phone, email, ticket type",
        "DataFlows creates contact with tags: Event Name, Ticket Type, Registration Date",
        "Add to 'Sydney Summit 2025 Attendees' group",
        "Send confirmation SMS immediately",
        "Send reminder SMS 7 days before event, then 1 day before event"
      ]
    },
    {
      title: "Form Submissions to SMS Database",
      industry: "Lead Generation",
      description: "Convert Australian web form leads into DataFlows contacts for SMS follow-up campaigns.",
      trigger: "Gravity Forms / Typeform - New Submission",
      steps: [
        "Prospect submits 'Request Quote' form on website",
        "Extract: Name, phone, email, service interested in, budget",
        "DataFlows creates contact with notes: Service, Budget, Source URL",
        "Add to 'Hot Leads - Quote Requests' group",
        "Sales team receives Slack notification",
        "Automated SMS: 'Thanks for your quote request! We'll call you within 30 mins.'"
      ]
    },
    {
      title: "Webinar Attendee Contact Management",
      industry: "Education & Training",
      description: "Capture webinar attendees from Zoom/GoToWebinar and create DataFlows contacts for future communication.",
      trigger: "Zoom - Webinar Registration",
      steps: [
        "User registers for 'Australian Tax Tips for Small Business' webinar",
        "Extract: Name, phone, email, company",
        "DataFlows creates contact tagged: Webinar Topic, Registration Date",
        "Add to 'Webinar Leads - Tax Tips' group",
        "Send SMS 1 hour before webinar: 'Starting soon! Join: [link]'",
        "Post-webinar SMS with recording link and special offer"
      ]
    },
    {
      title: "Patient Registration for Healthcare Practices",
      industry: "Healthcare & Medical",
      description: "Create DataFlows contacts from new patient registrations for appointment reminders and health updates.",
      trigger: "Acuity Scheduling - New Client",
      steps: [
        "New patient books first appointment",
        "Extract: Patient name, mobile, date of birth, preferred contact method",
        "DataFlows creates contact with medical consent flags",
        "Add to 'Active Patients' group",
        "Send welcome SMS with practice info and parking instructions",
        "Enable automated appointment reminder SMS"
      ]
    },
    {
      title: "Loyalty Program Member Sync",
      industry: "Retail & Hospitality",
      description: "Sync loyalty program signups from Square, Lightspeed, or custom apps to DataFlows for SMS rewards campaigns.",
      trigger: "Square - New Customer Created",
      steps: [
        "Customer signs up for loyalty program in-store",
        "Extract: Name, phone, email, loyalty tier (Bronze/Silver/Gold)",
        "DataFlows creates contact with loyalty tier tag",
        "Add to corresponding group: 'Gold Members', 'Silver Members', etc.",
        "Send SMS: 'Welcome to our VIP program! You've earned 100 points.'",
        "Send monthly SMS with exclusive offers based on tier"
      ]
    },
    {
      title: "Real Estate Inquiry Contact Builder",
      industry: "Real Estate",
      description: "Build Australian property buyer/seller contact lists from website inquiries and open home registrations.",
      trigger: "Gravity Forms - Property Inquiry",
      steps: [
        "Prospect submits interest form for property listing",
        "Extract: Name, phone, email, property interested in, budget range",
        "DataFlows creates contact with tags: Property Address, Budget, Inquiry Type",
        "Add to 'Active Buyers - [Suburb]' group",
        "Agent receives SMS alert with prospect details",
        "Send automated SMS to prospect: 'Thanks! Agent Sarah will call you within 15 mins.'"
      ]
    },
    {
      title: "Newsletter Subscriber to SMS List Conversion",
      industry: "Media & Publishing",
      description: "Convert email newsletter subscribers to SMS contacts for breaking news alerts and exclusive content.",
      trigger: "Mailchimp - New Subscriber",
      steps: [
        "Reader subscribes to newsletter and opts into SMS alerts",
        "Extract: Name, phone, email, interests/topics",
        "DataFlows creates contact tagged: Newsletter Subscriber, Topics of Interest",
        "Add to 'SMS News Alerts' group",
        "Send SMS: 'Thanks for subscribing! You'll get breaking news first. Reply STOP anytime.'",
        "Send SMS for urgent news stories only (1-2 per week maximum)"
      ]
    },
    {
      title: "Competition Entry Contact Collection",
      industry: "Marketing & Promotions",
      description: "Build SMS marketing lists from Australian competition entries and giveaways (with consent).",
      trigger: "Typeform - Competition Entry",
      steps: [
        "User enters competition: 'Win a $500 Gift Card'",
        "Extract: Name, phone, email, opt-in for marketing SMS",
        "Filter: Only create contact if marketing consent = Yes",
        "DataFlows creates contact tagged: Competition Name, Entry Date",
        "Add to 'Competition Entrants - June 2025' group",
        "Send SMS: 'Entered! Winner announced Friday. Opted in for exclusive offers (Reply STOP to opt out).'"
      ]
    },
    {
      title: "Booking Confirmation to Contact Database",
      industry: "Hospitality & Tourism",
      description: "Create contacts from restaurant, hotel, or tour bookings for future marketing and feedback requests.",
      trigger: "OpenTable - New Reservation",
      steps: [
        "Customer books table at 'The Harbour Restaurant'",
        "Extract: Name, phone, party size, special requests",
        "DataFlows creates contact with booking history",
        "Add to 'Harbour Restaurant Guests' group",
        "Send SMS 4 hours before: 'Looking forward to seeing you at 7PM!'",
        "Day after: SMS feedback request with Google review link"
      ]
    },
    {
      title: "Support Ticket Customer Contact Creation",
      industry: "Customer Support",
      description: "Automatically create DataFlows contacts from new support tickets for SMS status updates.",
      trigger: "Zendesk - New Ticket",
      steps: [
        "Customer submits support ticket via email",
        "Extract: Name, phone, email, ticket subject, priority",
        "DataFlows creates contact tagged: Ticket #, Issue Type, Priority",
        "Add to 'Support Customers' group",
        "Send SMS: 'Ticket #1234 created. We'll respond within 2 hours. Track: [link]'",
        "When ticket resolved: SMS with solution and satisfaction survey link"
      ]
    }
  ]

  return (
    <div className="zapier-feature-container">
      <DarkNav />
      <Helmet>
        <title>Create Contact via Zapier - DataFlows SMS Integration Australia</title>
        <meta
          name="description"
          content="Automatically create DataFlows SMS contacts from 6000+ apps with Zapier. Build SMS marketing lists from CRM, forms, e-commerce, and events. Australian SMS contact management automation."
        />
        <meta
          property="og:title"
          content="Create Contact via Zapier - DataFlows SMS Integration Australia"
        />
        <meta
          property="og:description"
          content="Automatically create DataFlows SMS contacts from 6000+ apps with Zapier. Build SMS marketing lists from CRM, forms, e-commerce, and events. Australian SMS contact management automation."
        />
        <link rel="canonical" href={getCanonicalUrl('/integrations/zapier/actions/create-contact')} />
      </Helmet>

      <div className="zapier-feature-hero" data-aos="fade-down">
        <div className="breadcrumbs-wrapper">
          <Breadcrumbs items={breadcrumbItems} />
        </div>
        <h1 className="zapier-feature-title">Create Contact</h1>
        <p className="zapier-feature-subtitle">
          Automatically add contacts to DataFlows from 6,000+ apps. Build SMS marketing lists from your CRM, forms, e-commerce platforms, and events.
        </p>
        <div className="zapier-feature-badge">
          <span className="badge-type">Action</span>
          <span className="badge-separator">•</span>
          <span className="badge-plan">All DataFlows Plans</span>
        </div>
      </div>

      <div className="zapier-feature-content">
        {/* Overview Section */}
        <section className="feature-section" data-aos="fade-up">
          <h2>What Does This Action Do?</h2>
          <p className="feature-description">
            The <strong>Create Contact</strong> action automatically adds new contacts to your DataFlows SMS contact database when triggered by events in your other apps. Perfect for Australian businesses wanting to build SMS marketing lists, sync CRM contacts, collect event attendees, or manage customer databases - all without manual data entry.
          </p>
          <div className="feature-highlight">
            <p><strong>Popular Australian Use Cases:</strong> Sync Salesforce/HubSpot leads, add Shopify customers to SMS lists, collect event registrations, build contact groups from web forms, create patient databases for healthcare, and more.</p>
          </div>
        </section>

        {/* How It Works */}
        <section className="feature-section" data-aos="fade-up">
          <h2>How It Works</h2>
          <div className="workflow-steps">
            <div className="workflow-step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3>Choose Your Trigger App</h3>
                <p>Select from 6,000+ apps like Salesforce, Shopify, Gravity Forms, Eventbrite, Mailchimp, Typeform, or Google Sheets as your contact source.</p>
              </div>
            </div>
            <div className="workflow-step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3>Configure DataFlows Create Contact Action</h3>
                <p>Map your trigger data to contact fields: name, phone number (required), email, custom tags, and contact group assignments.</p>
              </div>
            </div>
            <div className="workflow-step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3>Contact Created Automatically</h3>
                <p>When your trigger fires, DataFlows instantly creates the contact in your database with all mapped fields and group assignments.</p>
              </div>
            </div>
            <div className="workflow-step">
              <div className="step-number">4</div>
              <div className="step-content">
                <h3>Use for SMS Campaigns</h3>
                <p>Send targeted SMS campaigns to your auto-populated contact groups directly from DataFlows dashboard or via additional Zap automations.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Configuration Fields */}
        <section className="feature-section" data-aos="fade-up">
          <h2>Action Configuration Fields</h2>
          <div className="config-fields">
            <div className="config-field">
              <h4>Phone Number <span className="required-badge">Required</span></h4>
              <p>Contact's mobile number. Australian format: <code>0404235133</code> or <code>+61404235133</code>. International numbers supported with country code.</p>
            </div>
            <div className="config-field">
              <h4>First Name <span className="optional-badge">Optional</span></h4>
              <p>Contact's first name for personalization in SMS messages. Recommended for better engagement.</p>
            </div>
            <div className="config-field">
              <h4>Last Name <span className="optional-badge">Optional</span></h4>
              <p>Contact's last name. Helps with identification and professional communication.</p>
            </div>
            <div className="config-field">
              <h4>Email <span className="optional-badge">Optional</span></h4>
              <p>Contact's email address. Useful for multi-channel campaigns and contact verification.</p>
            </div>
            <div className="config-field">
              <h4>Contact Groups <span className="optional-badge">Optional</span></h4>
              <p>Assign contact to one or more groups (e.g., 'VIP Customers', 'Sydney Events', 'Newsletter Subscribers'). Create groups in DataFlows dashboard first.</p>
            </div>
            <div className="config-field">
              <h4>Tags <span className="optional-badge">Optional</span></h4>
              <p>Add custom tags for segmentation (e.g., 'Lead Source: Web Form', 'Industry: Healthcare', 'Budget: $10k+'). Comma-separated.</p>
            </div>
            <div className="config-field">
              <h4>Custom Fields <span className="optional-badge">Optional</span></h4>
              <p>Store additional data like customer ID, order history, preferences, or any custom fields you've created in DataFlows. Up to 10 custom fields supported.</p>
            </div>
            <div className="config-field">
              <h4>Opt-In Status <span className="optional-badge">Optional</span></h4>
              <p>Set marketing consent status: Opted In, Opted Out, or Pending. Important for Australian anti-spam compliance (Spam Act 2003).</p>
            </div>
          </div>
        </section>

        {/* Real-World Use Cases */}
        <section className="feature-section use-cases-section" data-aos="fade-up">
          <h2>Real-World Australian Use Cases</h2>
          <p className="section-intro">
            Here are {useCases.length} proven ways Australian businesses use the Create Contact action to automate contact management and build SMS marketing lists:
          </p>

          {useCases.map((useCase, index) => (
            <div key={index} className="use-case-card" data-aos="fade-up" data-aos-delay={index * 50}>
              <div className="use-case-header">
                <h3>{useCase.title}</h3>
                <span className="industry-tag">{useCase.industry}</span>
              </div>
              <p className="use-case-description">{useCase.description}</p>
              <div className="use-case-trigger">
                <strong>Trigger:</strong> {useCase.trigger}
              </div>
              <div className="workflow-breakdown">
                <h4>Workflow Breakdown:</h4>
                <ol className="workflow-steps-list">
                  {useCase.steps.map((step, stepIndex) => (
                    <li key={stepIndex}>{step}</li>
                  ))}
                </ol>
              </div>
            </div>
          ))}
        </section>

        {/* Setup Guide */}
        <section className="feature-section" data-aos="fade-up">
          <h2>Quick Setup Guide</h2>
          <div className="setup-steps">
            <h3>Step 1: Prepare Contact Groups in DataFlows</h3>
            <ol>
              <li>Log in to your DataFlows dashboard</li>
              <li>Navigate to "Contacts" → "Groups"</li>
              <li>Create groups you'll need (e.g., 'VIP Customers', 'Newsletter Subscribers', 'Event Attendees')</li>
              <li>Note the exact group names - you'll need these in Zapier</li>
            </ol>

            <h3>Step 2: Create Your Zap</h3>
            <ol>
              <li>Log in to Zapier and click "Create Zap"</li>
              <li>Search for and select your trigger app (e.g., "Salesforce", "Shopify", "Gravity Forms")</li>
              <li>Choose the trigger event (e.g., "New Lead", "New Customer", "New Form Submission")</li>
              <li>Connect your account and configure trigger settings</li>
              <li>Test the trigger to ensure data is flowing correctly</li>
            </ol>

            <h3>Step 3: Add DataFlows Create Contact Action</h3>
            <ol>
              <li>Click the "+" button to add an action step</li>
              <li>Search for "DataFlows SMS" and select it</li>
              <li>Choose the "Create Contact" action event</li>
              <li>Connect your DataFlows account (API key from DataFlows dashboard)</li>
              <li>Click "Continue"</li>
            </ol>

            <h3>Step 4: Map Contact Fields</h3>
            <ol>
              <li><strong>Phone Number:</strong> Map the phone field from your trigger (e.g., {'{'}{'{'} Mobile Phone {'}'}{'}'} from Salesforce)</li>
              <li><strong>First Name:</strong> Map {'{'}{'{'} First Name {'}'}{'}' } field</li>
              <li><strong>Last Name:</strong> Map {'{'}{'{'} Last Name {'}'}{'}' } field</li>
              <li><strong>Email:</strong> Map {'{'}{'{'} Email {'}'}{'}' } field</li>
              <li><strong>Contact Groups:</strong> Type or select your pre-created group names (e.g., "VIP Customers")</li>
              <li><strong>Tags:</strong> Add custom tags using static text or dynamic fields:
                <div className="code-example">
                  Lead Source: {'{'}{'{'} Lead Source {'}'}{'}'} , Budget: {'{'}{'{'} Budget Range {'}'}{'}'} , Date: {'{'}{'{'} Created Date {'}'}{'}' }
                </div>
              </li>
              <li><strong>Custom Fields:</strong> Map any additional data you want to store</li>
              <li>Click "Continue"</li>
            </ol>

            <h3>Step 5: Test & Activate</h3>
            <ol>
              <li>Click "Test action" to create a real contact in DataFlows</li>
              <li>Check your DataFlows dashboard to verify the contact was created correctly</li>
              <li>Verify contact group assignment and tags are correct</li>
              <li>Make any necessary adjustments to field mappings</li>
              <li>Turn on your Zap!</li>
              <li>Monitor the Zap history to ensure contacts are being created successfully</li>
            </ol>
          </div>
        </section>

        {/* Best Practices */}
        <section className="feature-section" data-aos="fade-up">
          <h2>Best Practices for Australian Businesses</h2>
          <div className="best-practices">
            <div className="practice-item">
              <h4>✅ Always Get Marketing Consent</h4>
              <p>Only create contacts who have explicitly opted in to receive SMS. Required by Australian Spam Act 2003. Add a checkbox to your forms: "I agree to receive SMS updates".</p>
            </div>
            <div className="practice-item">
              <h4>✅ Validate Phone Numbers</h4>
              <p>Use Zapier's "Formatter" tool before the Create Contact action to clean and validate phone numbers (remove spaces, check format, ensure Australian numbers).</p>
            </div>
            <div className="practice-item">
              <h4>✅ Prevent Duplicate Contacts</h4>
              <p>DataFlows automatically prevents duplicate phone numbers. If a contact already exists, the action will update it instead of creating a duplicate.</p>
            </div>
            <div className="practice-item">
              <h4>✅ Use Meaningful Group Names</h4>
              <p>Create specific, descriptive group names: "June 2025 Webinar Attendees" instead of "Group 3". Makes campaign targeting easier later.</p>
            </div>
            <div className="practice-item">
              <h4>✅ Tag Contacts with Source Data</h4>
              <p>Include useful tags like: Lead Source, Date Added, Campaign Name, Product Interest. Helps with segmentation and analytics.</p>
            </div>
            <div className="practice-item">
              <h4>✅ Send Welcome SMS Immediately</h4>
              <p>Add a "Send SMS" action after creating contact to welcome them and confirm opt-in. Include opt-out instructions: "Reply STOP to opt out".</p>
            </div>
            <div className="practice-item">
              <h4>✅ Store Custom Fields for Personalization</h4>
              <p>Map extra data like purchase history, preferences, birthday, location. Use in future SMS campaigns for better personalization.</p>
            </div>
            <div className="practice-item">
              <h4>✅ Use Filters to Qualify Contacts</h4>
              <p>Add Zapier "Filter" step before creating contact: Only create if phone number exists, only Australian numbers, only high-value leads, etc.</p>
            </div>
            <div className="practice-item">
              <h4>✅ Log Contact Creation for Auditing</h4>
              <p>Add a Google Sheets step to log all created contacts with timestamp. Helpful for compliance audits and tracking growth.</p>
            </div>
            <div className="practice-item">
              <h4>✅ Segment by Behaviour</h4>
              <p>Create different groups based on actions: "Downloaded Whitepaper", "Attended Webinar", "High Value Customers ($1000+)". Enables targeted SMS campaigns.</p>
            </div>
          </div>
        </section>

        {/* Troubleshooting */}
        <section className="feature-section" data-aos="fade-up">
          <h2>Common Issues & Solutions</h2>
          <div className="troubleshooting">
            <div className="issue-solution">
              <h4>❌ Contact Not Created</h4>
              <p><strong>Solutions:</strong></p>
              <ul>
                <li>Ensure phone number field is mapped and contains valid data</li>
                <li>Check phone number format is correct (Australian: 04XX XXX XXX or +614XX XXX XXX)</li>
                <li>Verify DataFlows API connection is active (re-authenticate if needed)</li>
                <li>Check Zap history for specific error messages</li>
              </ul>
            </div>
            <div className="issue-solution">
              <h4>❌ Contact Group Not Assigned</h4>
              <p><strong>Solutions:</strong></p>
              <ul>
                <li>Verify the group name is spelled exactly as it appears in DataFlows dashboard (case-sensitive)</li>
                <li>Create the group in DataFlows dashboard first before running the Zap</li>
                <li>If using dynamic group names, ensure the field contains valid group name text</li>
              </ul>
            </div>
            <div className="issue-solution">
              <h4>❌ Duplicate Contacts Being Updated Instead of Created</h4>
              <p><strong>Note:</strong> This is expected behaviour! DataFlows prevents duplicate phone numbers. If you want to update existing contacts, this is actually a feature, not a bug.</p>
            </div>
            <div className="issue-solution">
              <h4>❌ Tags Not Showing in DataFlows</h4>
              <p><strong>Solution:</strong> Ensure tags are comma-separated. Format: <code>Tag1, Tag2, Tag3</code> not <code>Tag1;Tag2;Tag3</code>. Check for typos or extra spaces.</p>
            </div>
            <div className="issue-solution">
              <h4>❌ Custom Fields Empty</h4>
              <p><strong>Solution:</strong> Create custom fields in DataFlows dashboard first. Navigate to Contacts → Custom Fields → Add Field. Then use the exact field name in Zapier mapping.</p>
            </div>
          </div>
        </section>

        {/* Advanced Tips */}
        <section className="feature-section" data-aos="fade-up">
          <h2>Advanced Automation Tips</h2>
          <div className="advanced-tips">
            <h3>Multi-Step Contact Enrichment</h3>
            <p>Enhance contact data before creating:</p>
            <ul>
              <li><strong>Validate Email:</strong> Use ZeroBounce or Clearout to verify email addresses</li>
              <li><strong>Lookup Company Data:</strong> Use Clearbit to enrich with company info, industry, size</li>
              <li><strong>Geocode Address:</strong> Use Google Maps to add suburb, postcode, coordinates</li>
              <li><strong>Score Leads:</strong> Use custom scoring logic to assign lead quality score</li>
            </ul>

            <h3>Conditional Group Assignment</h3>
            <p>Use Zapier Paths to assign contacts to different groups based on conditions:</p>
            <ul>
              <li>High-value customers ({'>'}$1000 spent) → "VIP Group"</li>
              <li>Sydney location → "Sydney Customers"</li>
              <li>Purchased Product A → "Product A Users"</li>
              <li>Lead score {'>'} 80 → "Hot Leads"</li>
            </ul>

            <h3>Automated Welcome Sequences</h3>
            <p>After creating contact, trigger multi-step SMS sequences:</p>
            <ul>
              <li>Immediate: Welcome SMS with opt-out instructions</li>
              <li>Day 3: Educational content or tips</li>
              <li>Day 7: Special offer or discount code</li>
              <li>Day 30: Feedback request or case study</li>
            </ul>

            <h3>CRM Two-Way Sync</h3>
            <p>Keep DataFlows and your CRM in sync:</p>
            <ul>
              <li>New Salesforce lead → Create DataFlows contact</li>
              <li>SMS sent in DataFlows → Log activity in Salesforce</li>
              <li>SMS reply received → Update Salesforce lead status</li>
              <li>Contact opts out → Update opt-out status in CRM</li>
            </ul>

            <h3>Bulk Import from Google Sheets</h3>
            <p>Migrate existing contact lists to DataFlows:</p>
            <ul>
              <li>Prepare Google Sheet with columns: Phone, First Name, Last Name, Email, Groups, Tags</li>
              <li>Trigger: Google Sheets - New/Updated Row</li>
              <li>Use "Looping by Zapier" to process multiple rows</li>
              <li>Add delays to respect API rate limits (1 second between creates)</li>
              <li>Mark processed rows to avoid duplicates</li>
            </ul>
          </div>
        </section>

        {/* Related Features */}
        <section className="feature-section related-features" data-aos="fade-up">
          <h2>Related DataFlows Features</h2>
          <div className="related-cards">
            <Link to="/integrations/zapier/actions/send-sms" className="related-card">
              <h4>Send SMS</h4>
              <p>Send automated SMS messages to your contacts from 6,000+ apps. Perfect for order confirmations, reminders, and alerts.</p>
              <span className="feature-type">Action</span>
            </Link>
            <Link to="/integrations/zapier/triggers/new-sms-received" className="related-card">
              <h4>New SMS Received</h4>
              <p>Trigger automations when contacts reply to your SMS messages. Enable two-way conversations.</p>
              <span className="feature-type">Trigger</span>
            </Link>
            <Link to="/integrations/zapier/actions/analyse-sentiment-ai" className="related-card">
              <h4>Analyse SMS Sentiment With AI</h4>
              <p>Use AI to detect customer emotions in SMS messages. Route angry customers to senior support automatically.</p>
              <span className="feature-type">Action</span>
            </Link>
          </div>
        </section>

        {/* CTA Section */}
        <section className="feature-section cta-section" data-aos="fade-up">
          <div className="cta-content">
            <h2>Ready to Automate Your Contact Management?</h2>
            <p>Join hundreds of Australian businesses using DataFlows + Zapier to build and manage SMS contact lists automatically.</p>
            <div className="cta-buttons">
              <a
                href="https://zapier.com/apps/dataflows-sms/integrations"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
              >
                Get Started with Zapier
              </a>
              <Link to="/integrations/zapier" className="btn-secondary">
                View All Features
              </Link>
            </div>
          </div>
        </section>
      </div>

      <Footer />

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="scroll-to-top"
          aria-label="Scroll to top"
        >
          ↑
        </button>
      )}
    </div>
  )
}

export default CreateContact
